/**
 * Saves.
 *
 * Requirements from the brief: multiple slots, autosave, manual save, load,
 * version field, basic corruption handling, and a migration path prepared.
 *
 * The design that makes all of that cheap: `GameState` is a plain data object
 * with no class instances, so a save is `JSON.stringify(state)` plus a header.
 * The only extra work is capturing the RNG and id-generator cursors, which is
 * what guarantees a loaded game continues identically rather than re-rolling.
 */
import type { GameState } from '../state/GameState.js';
import { BALANCE } from '../data/balance.js';

export const SAVE_VERSION = 5;

export interface SaveEnvelope {
  format: 'life-empire-save';
  version: number;
  savedAtIso: string;
  /** Cheap integrity check - not security, just corruption detection. */
  checksum: string;
  meta: SaveMeta;
  state: GameState;
}

export interface SaveMeta {
  slot: string;
  day: number;
  clock: string;
  cash: number;
  netWorth: number;
  businessName: string;
  reputation: number;
}

export type LoadResult =
  | { ok: true; state: GameState; migratedFrom?: number }
  | { ok: false; error: string };

/** A migration takes a save one version forward. */
type Migration = (raw: Record<string, unknown>) => Record<string, unknown>;

/**
 * Registry of migrations, keyed by the version they upgrade FROM.
 *
 * A migration must never assume the shape it is handed is valid - it runs on
 * whatever was on disk, including files written by builds that had bugs. Read
 * defensively, write completely.
 */
const MIGRATIONS: Record<number, Migration> = {
  /**
   * v1 -> v2: the drivable vehicle (ADR-0011).
   *
   * v1 knew which vehicle you owned but not where it was, because it was not
   * a thing you could move. A v1 save therefore has no parked position, and
   * the right answer is null: the client places an owned vehicle on the
   * driveway when it finds none recorded, which is exactly where a v1 player
   * last saw it. Fuel starts full rather than empty, so loading an old save
   * does not strand the player at a pump they have never needed before.
   */
  1: (raw) => {
    const player = (raw.player ?? {}) as Record<string, unknown>;
    return {
      ...raw,
      player: {
        ...player,
        vehicleParked: player.vehicleParked ?? null,
        fuel: typeof player.fuel === 'number'
          ? player.fuel
          : (player.vehicleId ? BALANCE.vehicle.fuelCapacity : 0),
      },
    };
  },

  /**
   * v2 -> v3: customer traits (Priority 8).
   *
   * Existing customers get traits derived from their id rather than drawn at
   * random. Random values would make loading the same save twice produce two
   * different neighbourhoods, and a save that changes when you reload is a
   * save nobody can trust. The hash is crude on purpose - it only has to be
   * stable and spread out, not uniform.
   */
  2: (raw) => {
    const customers = Array.isArray(raw.customers) ? raw.customers : [];
    return {
      ...raw,
      customers: customers.map((entry) => {
        const c = entry as Record<string, unknown>;
        if (c.traits) return c;
        const id = typeof c.id === 'string' ? c.id : '';
        let h = 2166136261;
        for (let i = 0; i < id.length; i++) {
          h ^= id.charCodeAt(i);
          h = Math.imul(h, 16777619);
        }
        const at = (shift: number): number =>
          (((h >>> shift) & 0xff) / 255);
        return {
          ...c,
          traits: {
            expectation: 0.2 + at(0) * 0.75,
            patience: 0.25 + at(8) * 0.75,
            generosity: at(16),
            difficulty: 0.15 + at(24) * 0.85,
          },
        };
      }),
    };
  },

  /**
   * v3 -> v4: a yard job in progress gains its task list.
   *
   * Only `player.activeWork` is affected, and only when the save was taken
   * mid-job. The tasks cannot be regenerated faithfully - they depend on what
   * the player owned at the moment they started, which the save does not
   * record separately - so the job becomes a single mow task at the coverage
   * it already had. That is exactly the job it was when it was saved.
   */
  3: (raw) => {
    const player = raw.player as Record<string, unknown> | undefined;
    if (!player) return raw;
    const work = player.activeWork as Record<string, unknown> | null | undefined;
    if (!work || Array.isArray(work.tasks)) return raw;
    const coverage = typeof work.coverage === 'number' ? work.coverage : 1;
    return {
      ...raw,
      player: {
        ...player,
        activeWork: {
          ...work,
          tasks: [{ kind: 'mow', weight: 1, progress: coverage }],
        },
      },
    };
  },

  /**
   * v4 -> v5: equipment gains a condition record.
   *
   * An empty map, not a filled one. A missing entry already reads as new
   * everywhere in `BusinessSystem`, so an existing save's kit arrives in
   * working order - which is the right answer: it was working when they
   * saved, and starting someone's tools half worn because the game learned a
   * new mechanic would be a punishment for playing early.
   */
  4: (raw) => {
    const business = raw.business as Record<string, unknown> | undefined;
    if (!business || business.equipmentCondition) return raw;
    return { ...raw, business: { ...business, equipmentCondition: {} } };
  },
};

export function checksumOf(state: GameState): string {
  const json = JSON.stringify(state);
  // FNV-1a, 32-bit. Enough to notice a truncated or edited file.
  let h = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    h ^= json.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

export function serialise(state: GameState, meta: SaveMeta): string {
  const envelope: SaveEnvelope = {
    format: 'life-empire-save',
    version: SAVE_VERSION,
    savedAtIso: new Date().toISOString(),
    checksum: checksumOf(state),
    meta,
    state,
  };
  return JSON.stringify(envelope);
}

export function deserialise(text: string): LoadResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: 'Save file is not valid JSON (corrupted).' };
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return { ok: false, error: 'Save file is empty or malformed.' };
  }
  const env = parsed as Partial<SaveEnvelope> & Record<string, unknown>;
  if (env.format !== 'life-empire-save') {
    return { ok: false, error: 'Not a LIFE EMPIRE save file.' };
  }
  if (typeof env.version !== 'number') {
    return { ok: false, error: 'Save file has no version field.' };
  }
  if (env.version > SAVE_VERSION) {
    return {
      ok: false,
      error: `Save was written by a newer version (v${env.version}); this build understands up to v${SAVE_VERSION}.`,
    };
  }
  if (!env.state || typeof env.state !== 'object') {
    return { ok: false, error: 'Save file has no game state.' };
  }

  const originalVersion = env.version;
  let raw = env.state as unknown as Record<string, unknown>;
  let version = env.version;
  while (version < SAVE_VERSION) {
    const migrate = MIGRATIONS[version];
    if (!migrate) {
      return { ok: false, error: `No migration path from save version ${version}.` };
    }
    raw = migrate(raw);
    version += 1;
  }

  const state = raw as unknown as GameState;
  if (!isPlausibleState(state)) {
    return { ok: false, error: 'Save file is missing required fields (corrupted).' };
  }

  // A mismatched checksum means the file was edited or truncated. We surface it
  // rather than refusing outright when the state still parses, because losing a
  // long game to a strict check is worse than loading a slightly odd one - but
  // an unreadable state above has already been rejected.
  if (originalVersion === SAVE_VERSION && typeof env.checksum === 'string') {
    if (checksumOf(state) !== env.checksum) {
      return { ok: false, error: 'Save file failed its integrity check (corrupted).' };
    }
  }

  state.version = SAVE_VERSION;
  return originalVersion === SAVE_VERSION
    ? { ok: true, state }
    : { ok: true, state, migratedFrom: originalVersion };
}

function isPlausibleState(state: unknown): state is GameState {
  if (typeof state !== 'object' || state === null) return false;
  const s = state as Record<string, unknown>;
  const time = s.time as Record<string, unknown> | undefined;
  const finance = s.finance as Record<string, unknown> | undefined;
  const player = s.player as Record<string, unknown> | undefined;
  const business = s.business as Record<string, unknown> | undefined;
  return (
    !!time && typeof time.totalMinutes === 'number' &&
    !!finance && typeof finance.cash === 'number' &&
    !!player && typeof player.reputation === 'number' &&
    !!business && Array.isArray(business.equipment) &&
    Array.isArray(s.customers) &&
    Array.isArray(s.leads)
  );
}

/** Storage abstraction: localStorage in the browser, memory or disk elsewhere. */
export interface SaveStorage {
  read(key: string): string | null;
  write(key: string, value: string): void;
  remove(key: string): void;
  keys(): string[];
}

export class MemoryStorage implements SaveStorage {
  private map = new Map<string, string>();
  read(key: string) { return this.map.get(key) ?? null; }
  write(key: string, value: string) { this.map.set(key, value); }
  remove(key: string) { this.map.delete(key); }
  keys() { return [...this.map.keys()]; }
}

export const SLOT_PREFIX = 'life-empire:save:';
export const MANUAL_SLOTS = ['slot1', 'slot2', 'slot3'] as const;
export const AUTOSAVE_SLOT = 'autosave';
export const ALL_SLOTS = [...MANUAL_SLOTS, AUTOSAVE_SLOT] as const;

export class SaveSystem {
  constructor(private readonly storage: SaveStorage) {}

  save(slot: string, state: GameState, meta: SaveMeta): boolean {
    try {
      this.storage.write(SLOT_PREFIX + slot, serialise(state, { ...meta, slot }));
      return true;
    } catch {
      // Quota exceeded, private-mode storage, read-only disk: all end here.
      return false;
    }
  }

  load(slot: string): LoadResult {
    const text = this.storage.read(SLOT_PREFIX + slot);
    if (text === null) return { ok: false, error: 'That slot is empty.' };
    return deserialise(text);
  }

  remove(slot: string): void {
    this.storage.remove(SLOT_PREFIX + slot);
  }

  /** Slot headers for the load menu, without deserialising whole games. */
  list(): (SaveMeta & { version: number; savedAtIso: string; corrupt: boolean })[] {
    const out: (SaveMeta & { version: number; savedAtIso: string; corrupt: boolean })[] = [];
    for (const slot of ALL_SLOTS) {
      const text = this.storage.read(SLOT_PREFIX + slot);
      if (text === null) continue;
      try {
        const env = JSON.parse(text) as SaveEnvelope;
        if (env.format !== 'life-empire-save' || !env.meta) throw new Error('bad');
        out.push({
          ...env.meta,
          slot,
          version: env.version,
          savedAtIso: env.savedAtIso,
          corrupt: false,
        });
      } catch {
        out.push({
          slot, day: 0, clock: '--:--', cash: 0, netWorth: 0,
          businessName: '(unreadable)', reputation: 0,
          version: 0, savedAtIso: '', corrupt: true,
        });
      }
    }
    return out;
  }
}
